/**
 * @fileoverview Boter Kaas en Eieren game types
 * @description Type definitions for the Boter Kaas en Eieren game module
 */

/** Cell value: X, O, or empty */
export type CellValue = "X" | "O" | null;

/** Player symbol */
export type PlayerSymbol = "X" | "O";

/** Game status */
export type GameStatus = "waiting" | "playing" | "finished" | "draw";

/** Board position (0-8 for a 3x3 grid) */
export type BoardPosition = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** The game board as a 9-cell array */
export type Board = [
  CellValue,
  CellValue,
  CellValue,
  CellValue,
  CellValue,
  CellValue,
  CellValue,
  CellValue,
  CellValue
];

/** Win line definition */
export interface WinLine {
  positions: [BoardPosition, BoardPosition, BoardPosition];
  winner: PlayerSymbol;
}

/** Player info in a game */
export interface TicTacToePlayer {
  id: string;
  name: string;
  avatar_url?: string | null;
  symbol: PlayerSymbol;
  is_current_turn: boolean;
}

/** Game state stored in game_sessions.game_state */
export interface TicTacToeGameState {
  board: Board;
  current_turn: PlayerSymbol;
  status: GameStatus;
  winner: PlayerSymbol | null;
  win_line: WinLine | null;
  move_count: number;
  last_move_at: string | null;
  players: {
    X: string; // user id
    O: string; // user id or 'ai' for bot
  };
  move_history: Array<{
    position: BoardPosition;
    symbol: PlayerSymbol;
    timestamp: string;
  }>;
}

/** Props for the game board component */
export interface TicTacToeBoardProps {
  board: Board;
  disabled: boolean;
  winLine: WinLine | null;
  onCellPress: (position: BoardPosition) => void;
  isMyTurn: boolean;
  animatingCell?: BoardPosition | null;
}

/** Props for a single cell */
export interface TicTacToeCellProps {
  value: CellValue;
  position: BoardPosition;
  disabled: boolean;
  isWinningCell: boolean;
  onPress: () => void;
  isAnimating: boolean;
}

/** Game result for stats tracking */
export interface TicTacToeResult {
  winner_id: string | null;
  loser_id: string | null;
  is_draw: boolean;
  move_count: number;
  duration_seconds: number;
}

/** AI difficulty levels */
export type AIDifficulty = "easy" | "medium" | "hard";

/** Game settings */
export interface TicTacToeSettings {
  ai_difficulty?: AIDifficulty;
  time_limit_seconds?: number;
  allow_undo?: boolean;
}

/** Initial empty board */
export const EMPTY_BOARD: Board = [
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  null,
];

/** All possible winning combinations */
export const WIN_COMBINATIONS: Array<
  [BoardPosition, BoardPosition, BoardPosition]
> = [
  [0, 1, 2], // Top row
  [3, 4, 5], // Middle row
  [6, 7, 8], // Bottom row
  [0, 3, 6], // Left column
  [1, 4, 7], // Middle column
  [2, 5, 8], // Right column
  [0, 4, 8], // Diagonal top-left to bottom-right
  [2, 4, 6], // Diagonal top-right to bottom-left
];

